import { DownloadSimple, Plus, Prohibit, ArrowsLeftRight, ListChecks, X, CaretDown, CaretRight } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const money = (v) => (v || v === 0 ? inr(v) : "—");
const nz = (v) => Number(v || 0);

/** One editable amount cell. */
const Cell = ({ value, onChange, disabled, testid }) => (
  <input
    type="number" step="0.01" value={value === 0 ? "" : value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    disabled={disabled} data-testid={testid} placeholder="0"
    className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-right text-xs tabular-nums text-navy outline-none transition hover:border-border focus:border-terracotta focus:bg-card disabled:cursor-not-allowed"
  />
);

const rowTotal = (row) => Object.entries(row.values || {})
  .reduce((s, [, v]) => s + nz(v), 0);

/** A single grid (a sheet, or one COGS derivation table). */
const Grid = ({ container, sheetKey, edits, onEdit, onExclude, onMove, moveOptions,
                locked, canEdit, breakups, onToggleBreakup, onAdd }) => {
  const columns = container.columns || [];
  const rows = container.rows || [];
  const valueOf = (row, col) => {
    const patch = edits[row.key];
    if (patch && patch[col] !== undefined) return patch[col];
    return nz(row.values?.[col]);
  };
  const colsOf = (row) => row.block_columns || columns;
  const isBrokenUp = (row) => !!(breakups?.has(row.key) || row.broken_up);
  const childrenOf = (row) => rows.filter((r) => r.kind === "detail" && r.parent_key === row.key);
  // a broken-up component row is worth the live sum of its ledger lines, not its stale cells
  const effValue = (row, col) => {
    if (row.kind === "line" && isBrokenUp(row)) {
      const kids = childrenOf(row).filter((r) => !r.excluded);
      if (kids.length) return kids.reduce((s, r) => s + nz(valueOf(r, col)), 0);
    }
    return valueOf(row, col);
  };
  const liveRow = (row) => colsOf(row).reduce((s, c) => s + nz(effValue(row, c)), 0);
  const lines = rows.filter((r) => r.kind === "line" && !r.excluded);
  const grand = lines.reduce((s, r) => s + liveRow(r), 0);

  // block subtotals: the live lines between this row and the section header above it
  const blockLines = (index) => {
    const out = [];
    for (let i = index - 1; i >= 0; i -= 1) {
      const r = rows[i];
      if (r.kind === "section") break;
      if (r.kind === "line" && !r.excluded) out.push(r);
    }
    return out;
  };
  const sumOf = (list, col) => list.filter((r) => colsOf(r).includes(col))
    .reduce((s, r) => s + nz(effValue(r, col)), 0);
  const totalOf = (list) => list.reduce((s, r) => s + liveRow(r), 0);

  return (
    <div className="overflow-x-auto" data-testid={`fp-recon-grid-${sheetKey}-${container.key || "main"}`}>
      {container.title && (
        <p className="label-caps px-5 pt-4">{container.title}</p>
      )}
      <table className="w-full min-w-[46rem] text-xs">
        <thead>
          <tr className="sticky top-0 bg-muted/60 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            <th className="px-4 py-2.5 text-left">Particulars</th>
            {columns.map((c) => (
              <th key={c} className="px-2 py-2.5 text-right" data-testid={`fp-recon-col-${c}`}>{c}</th>
            ))}
            <th className="px-3 py-2.5 text-right">Total</th>
            {canEdit && <th className="px-2 py-2.5 text-right">·</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, index) => {
            const cols = row.block_columns || columns;
            if (row.kind === "section") {
              return (
                <tr key={row.key} className="bg-navy/[0.04]" data-testid={`fp-recon-section-${row.key}`}>
                  <td className="px-4 py-2 font-display text-sm text-navy" colSpan={columns.length + 2 + (canEdit ? 1 : 0)}>
                    {row.label}
                    {cols.length > 1 && cols !== columns && (
                      <span className="ml-2 text-[10px] font-normal uppercase tracking-[0.1em] text-muted-foreground">
                        split by {cols.slice(0, 2).join(", ")}…
                      </span>
                    )}
                  </td>
                </tr>
              );
            }
            if (row.kind === "detail") {
              // a ledger line inside a broken-up component row — editable, rolls up to its parent
              const parent = rows.find((r) => r.key === row.parent_key);
              if (!parent || !isBrokenUp(parent)) return null;
              const dExcluded = !!row.excluded;
              return (
                <tr key={row.key}
                    className={`bg-muted/20 transition-colors ${dExcluded ? "opacity-40" : ""}`}
                    data-testid={`fp-recon-detail-${row.key}`}>
                  <td className="px-4 py-1 text-[11px] text-navy/80"
                      style={{ paddingLeft: 16 + (parent.indent || 0) * 14 + 26 }}>
                    <span className={dExcluded ? "line-through" : ""}>{row.label}</span>
                  </td>
                  {columns.map((c) => {
                    const inBlock = cols.includes(c);
                    return (
                      <td key={c} className="px-1 py-0.5">
                        {inBlock ? (
                          <Cell value={valueOf(row, c)} disabled={locked || !canEdit || dExcluded}
                                testid={`fp-recon-cell-${row.key}-${c}`}
                                onChange={(v) => onEdit(row.key, c, v)} />
                        ) : <span className="block px-2 text-right text-muted-foreground">·</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1 text-right text-[11px] font-medium tabular-nums text-navy/80">
                    {money(liveRow(row))}
                  </td>
                  {canEdit && (
                    <td className="whitespace-nowrap px-2 py-1 text-right">
                      <button onClick={() => onExclude(row.key, !dExcluded)} disabled={locked}
                              title={dExcluded ? "Bring back" : "Set aside"}
                              data-testid={`fp-recon-exclude-${row.key}`}
                              className="rounded-full border border-border p-1 text-muted-foreground transition hover:bg-muted disabled:opacity-40">
                        <Prohibit size={11} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            }
            if (row.kind !== "line") {
              // a section subtotal covers its own block; a sheet total covers every live line
              const scope = row.kind === "subtotal" ? blockLines(index)
                : container.result_key === row.key
                  ? rows.filter((r) => (container.line_keys || []).includes(r.key) && !r.excluded)
                  : lines;
              const signed = container.result_key === row.key;
              const value = signed
                ? scope.reduce((s, r) => s + liveRow(r) * (r.negative ? -1 : 1), 0)
                : totalOf(scope);
              return (
                <tr key={row.key} className="bg-muted/40 font-semibold text-navy"
                    data-testid={`fp-recon-total-${row.key}`}>
                  <td className="px-4 py-2">{row.label}</td>
                  {columns.map((c) => (
                    <td key={c} className="px-2 py-2 text-right tabular-nums">
                      {signed
                        ? money(scope.filter((r) => colsOf(r).includes(c))
                            .reduce((s, r) => s + nz(valueOf(r, c)) * (r.negative ? -1 : 1), 0))
                        : money(sumOf(scope, c))}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums">{money(value)}</td>
                  {canEdit && <td />}
                </tr>
              );
            }
            const excluded = !!row.excluded;
            return (
              <tr key={row.key}
                  className={`transition-colors hover:bg-muted/30 ${excluded ? "opacity-40" : ""}`}
                  data-testid={`fp-recon-row-${row.key}`}>
                <td className="px-4 py-1.5 text-navy" style={{ paddingLeft: 16 + (row.indent || 0) * 14 }}>
                  {row.has_breakup && (isBrokenUp(row) || !locked) && (
                    <button type="button"
                            onClick={() => onToggleBreakup && onToggleBreakup(row.key)}
                            title={isBrokenUp(row)
                              ? "Collapse — return to the component figure"
                              : canEdit
                                ? "Break up into books ledgers (editable)"
                                : "View the books ledger breakup"}
                            data-testid={`fp-recon-breakup-${row.key}`}
                            className="mr-1.5 inline-flex items-center rounded-full border border-border p-0.5 text-muted-foreground transition hover:bg-muted">
                      {isBrokenUp(row) ? <CaretDown size={10} /> : <CaretRight size={10} />}
                    </button>
                  )}
                  <span className={excluded ? "line-through" : ""}>{row.label}</span>
                  {isBrokenUp(row) && childrenOf(row).length > 0 && (
                    <span className="ml-2 rounded-full bg-navy/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-navy">
                      sum of {childrenOf(row).filter((k) => !k.excluded).length} ledgers
                    </span>
                  )}
                  {row.books_only && <span className="ml-2 rounded-full bg-warning/25 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[hsl(30_88%_30%)]">books only</span>}
                  {row.added && <span className="ml-2 rounded-full bg-terracotta/15 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-terracotta">added</span>}
                  {row.unnamed && <span className="ml-2 rounded-full bg-warning/25 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[hsl(30_88%_30%)]">not in design</span>}
                  {row.moved_to && <span className="ml-2 text-[10px] text-muted-foreground">→ {row.moved_to}</span>}
                </td>
                {columns.map((c) => {
                  const inBlock = cols.includes(c);
                  return (
                    <td key={c} className="px-1 py-0.5">
                      {inBlock ? (
                        <Cell value={effValue(row, c)}
                              disabled={locked || !canEdit || excluded || isBrokenUp(row)}
                              testid={`fp-recon-cell-${row.key}-${c}`}
                              onChange={(v) => onEdit(row.key, c, v)} />
                      ) : <span className="block px-2 text-right text-muted-foreground">·</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 text-right font-medium tabular-nums text-navy">
                  {money(liveRow(row))}
                </td>
                {canEdit && (
                  <td className="whitespace-nowrap px-2 py-1.5 text-right">
                    {moveOptions?.length > 0 && (
                      <select value={row.moved_to || ""} disabled={locked}
                              data-testid={`fp-recon-move-${row.key}`}
                              onChange={(e) => onMove(row.key, e.target.value)}
                              className="mr-1 rounded-md border border-border bg-card px-1 py-0.5 text-[10px] text-navy">
                        <option value="">move…</option>
                        {moveOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    <button onClick={() => onExclude(row.key, !excluded)} disabled={locked}
                            title={excluded ? "Bring back" : "Set aside"}
                            data-testid={`fp-recon-exclude-${row.key}`}
                            className="rounded-full border border-border p-1 text-muted-foreground transition hover:bg-muted disabled:opacity-40">
                      <Prohibit size={11} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && !locked && onAdd && (
        <div className="border-t border-border px-4 py-2">
          <button onClick={() => onAdd(container)}
                  data-testid={`fp-recon-add-line-${container.key || "main"}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted">
            <Plus size={11} /> Add a line under {container.title || "this section"}
          </button>
        </div>
      )}
    </div>
  );
};

const CHECKLIST = [
  { title: "Sheets load and tie to the engine",
    hint: "Open all five tabs — each shows its grid with the totals strip (Sheet says / Books control / Difference / Published now)." },
  { title: "Live recomputation",
    hint: "Change any cell on a draft sheet: row, column and grand totals — and \"Sheet says\" — move instantly as you type." },
  { title: "Draft survives navigation",
    hint: "With an unsaved edit on screen, switch the month away and back — the edit is still there. Save draft, hard-refresh — still there." },
  { title: "Discard reverts cleanly",
    hint: "Discard draft returns the grid to the engine figures and clears your test edit everywhere." },
  { title: "Approval locks and publishes",
    hint: "An approved sheet is read-only, the header reads \"Approved — this sheet is the published figure\", and Published now matches Level 1 in the Financial Model." },
  { title: "What changes preview",
    hint: "Edit a draft cell, click What changes — current vs projected EBITDA / throughput appear side by side before you commit." },
  { title: "Excel export",
    hint: "Download (.xlsx) produces a workbook with five sheets mirroring the grids." },
  { title: "Client view",
    hint: "Sign in as the client (ceo@…): same approved figures, drafts editable, and no Approve sheet button — approval stays with the consultant." },
];

/** Self-test checklist overlay — lets a consultant or client verify the workspace hands-on. */
const ChecklistModal = ({ onClose }) => {
  const [done, setDone] = useState({});
  const count = CHECKLIST.reduce((n, _, i) => n + (done[i] ? 1 : 0), 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
      <div className="panel max-h-[85vh] w-full max-w-xl overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()} data-testid="fp-recon-checklist">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-caps">Self-test</p>
            <h3 className="mt-1 font-display text-lg text-navy">Reconciliation checklist</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Tick each step as it passes on your screen — {count} of {CHECKLIST.length} verified.
            </p>
          </div>
          <button onClick={onClose} data-testid="fp-recon-checklist-close"
                  className="rounded-full border border-border p-1.5 text-muted-foreground transition hover:bg-muted">
            <X size={14} />
          </button>
        </div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-terracotta transition-all"
               style={{ width: `${(count / CHECKLIST.length) * 100}%` }}
               data-testid="fp-recon-checklist-progress" />
        </div>
        <div className="mt-4 space-y-2">
          {CHECKLIST.map((item, i) => (
            <label key={item.title} data-testid={`fp-recon-checklist-item-${i}`}
                   className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition hover:bg-muted/40">
              <input type="checkbox" checked={!!done[i]}
                     onChange={() => setDone((d) => ({ ...d, [i]: !d[i] }))}
                     className="mt-0.5 h-4 w-4 accent-terracotta" />
              <span>
                <span className={`block text-sm font-medium ${done[i] ? "text-muted-foreground line-through" : "text-navy"}`}>
                  {i + 1}. {item.title}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{item.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <p className="mt-4 rounded-lg bg-muted/40 p-3 text-[11px] text-muted-foreground">
          A non-zero Difference pill is expected — it is the books-vs-sheet gap the reconciliation
          exists to close, not an error.
        </p>
      </div>
    </div>
  );
};

/** How the books anchor was built — the leaf lines behind it plus the known gap reasons. */
const DerivationModal = ({ detail, sheetLabel, sheetTotal, control, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
    <div className="panel max-h-[85vh] w-full max-w-2xl overflow-y-auto p-6"
         onClick={(e) => e.stopPropagation()} data-testid="fp-recon-derivation">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-caps">{sheetLabel} · books control total</p>
          <h3 className="mt-1 font-display text-lg text-navy">How this anchor is built</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Source: {detail?.source || "—"}
            {detail?.line_count ? ` · ${detail.line_count} classified leaf lines` : ""}
          </p>
        </div>
        <button onClick={onClose} data-testid="fp-recon-derivation-close"
                className="rounded-full border border-border p-1.5 text-muted-foreground transition hover:bg-muted">
          <X size={14} />
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {[["Books control", control], ["Sheet says", sheetTotal],
          ["Difference", Math.round((nz(sheetTotal) - nz(control)) * 100) / 100]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-muted/40 p-3">
            <p className="label-caps">{l}</p>
            <p className="mt-1 font-display text-base text-navy tabular-nums">{money(v)}</p>
          </div>
        ))}
      </div>

      {!!detail?.lines?.length && (
        <div className="mt-5 overflow-x-auto">
          <p className="label-caps">Largest lines in the anchor</p>
          <table className="mt-2 w-full text-xs" data-testid="fp-recon-derivation-lines">
            <tbody className="divide-y divide-border">
              {detail.lines.map((l, i) => (
                <tr key={`${l.label}-${i}`} data-testid={`fp-recon-derivation-line-${i}`}>
                  <td className="py-1.5 pr-3 text-navy">
                    {l.label}
                    {l.component && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                        {l.component}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-navy">{money(l.amount)}</td>
                </tr>
              ))}
              {Math.abs(nz(detail.other)) > 0.5 && (
                <tr className="text-muted-foreground" data-testid="fp-recon-derivation-other">
                  <td className="py-1.5 pr-3">
                    All other classified lines ({Math.max(detail.line_count - detail.lines.length, 0)})
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{money(detail.other)}</td>
                </tr>
              )}
              <tr className="bg-muted/40 font-semibold text-navy">
                <td className="py-2 pr-3">Books control total</td>
                <td className="py-2 text-right tabular-nums">{money(detail.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!!detail?.notes?.length && (
        <div className="mt-5 rounded-xl border border-warning/50 bg-warning/10 p-4"
             data-testid="fp-recon-derivation-notes">
          <p className="label-caps">Why the sheet legitimately differs</p>
          <ul className="mt-2 space-y-2 text-[11px] leading-relaxed text-[hsl(30_88%_26%)]">
            {detail.notes.map((n) => <li key={n}>· {n}</li>)}
          </ul>
        </div>
      )}
    </div>
  </div>
);

export default function FpReconGrids() {
  const { tenantId, period } = useFpContext();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("revenue");
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [impact, setImpact] = useState(null);
  const [netFg, setNetFg] = useState("");

  const storeKey = `fp-recon-drafts:${tenantId}:${period}`;
  const [showChecklist, setShowChecklist] = useState(false);
  const [showDerivation, setShowDerivation] = useState(false);
  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: d } = await api.get(`${fpBase(tenantId)}/recon-grids`, { params: { period } });
      setData(d);
      setNetFg(d?.sheets?.cogs?.net_fg_published ?? "");
      // unsaved edits live in the browser per engagement+month — but a stale local draft must
      // never sit on top of a sheet that has since been approved on the server
      let stored = {};
      try { stored = JSON.parse(localStorage.getItem(`fp-recon-drafts:${tenantId}:${period}`) || "{}"); } catch { /* ignore */ }
      const kept = {};
      for (const [k, v] of Object.entries(stored)) {
        if (d?.sheets?.[k]?.status !== "approved") kept[k] = v;
      }
      if (Object.keys(kept).length !== Object.keys(stored).length) {
        try { localStorage.setItem(`fp-recon-drafts:${tenantId}:${period}`, JSON.stringify(kept)); } catch { /* ignore */ }
      }
      setDrafts(kept);
      setImpact(null);
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId, period]);

  useEffect(() => { load(); }, [load]);

  const sheet = data?.sheets?.[active];
  const draft = drafts[active] || { cells: {}, excluded: [], moves: {}, added: [] };
  const locked = sheet?.status === "approved";
  const canEdit = !!data?.can_edit;

  const patch = (fn) => setDrafts((d) => {
    const cur = d[active] || { cells: {}, excluded: [], moves: {}, added: [] };
    const next = { ...d, [active]: fn(cur) };
    try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const clearStored = (sheetKey) => {
    try {
      const cur = JSON.parse(localStorage.getItem(storeKey) || "{}");
      delete cur[sheetKey];
      localStorage.setItem(storeKey, JSON.stringify(cur));
    } catch { /* ignore */ }
  };

  const onEdit = (key, col, value) => patch((cur) => ({
    ...cur, cells: { ...cur.cells, [key]: { ...(cur.cells[key] || {}), [col]: value } } }));
  const onExclude = (key, off) => patch((cur) => ({
    ...cur,
    excluded: off ? [...new Set([...cur.excluded, key])] : cur.excluded.filter((k) => k !== key) }));
  const onMove = (key, to) => patch((cur) => ({ ...cur, moves: { ...cur.moves, [key]: to } }));

  const addLine = (container) => {
    const where = container?.title ? ` under "${container.title}"` : "";
    const label = window.prompt(`Name of the missing line${where}`);
    if (!label?.trim()) return;
    const slug = label.trim().toLowerCase().replace(/\s+/g, "-");
    const ckey = container?.key || "main";
    const key = `added::${active}::${ckey}::${slug}`;
    const entry = { key, label: label.trim(), values: {}, container: ckey };
    if (active === "fixed_cost") {
      entry.dimension = "function";
      entry.dimension_value = container?.key || null;
    }
    patch((cur) => ({ ...cur, added: [...cur.added, entry] }));
    toast.success(`"${label.trim()}" added${where} — type its figures, then save`);
  };

  const body = () => ({ period, ...draft, breakups: [...breakupSet],
    excluded: [...draft.excluded, ...((sheet?.rows || []).filter((r) => r.excluded).map((r) => r.key))] });

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`${fpBase(tenantId)}/recon-grids/${active}`, body());
      clearStored(active);
      toast.success(`${sheet.label} draft saved`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const saveNetFg = async () => {
    setBusy("netfg");
    try {
      const { data: d } = await api.post(`${fpBase(tenantId)}/recon-grids/cogs/net-fg`,
        { period, net: nz(netFg) });
      toast.success(`Net FG stock ${inr(d.net_fg)} published for ${monthLabel(period)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const preview = async () => {
    setBusy("preview");
    try {
      const { data: d } = await api.post(`${fpBase(tenantId)}/recon-grids/preview`,
        { period, sheets: { [active]: draft } });
      setImpact(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const approve = async () => {
    if (!window.confirm(`Approve ${sheet.label}? From now on the published figures for this area are exactly what this sheet says.`)) return;
    setBusy("approve");
    try {
      await api.post(`${fpBase(tenantId)}/recon-grids/${active}/approve`, body());
      clearStored(active);
      toast.success(`${sheet.label} approved — it is now the authority for this month`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const act = async (what) => {
    setBusy(what);
    try {
      await api.post(`${fpBase(tenantId)}/recon-grids/${active}/${what}`, { period });
      if (what === "discard") clearStored(active);
      toast.success(what === "reopen" ? "Sheet re-opened" : "Draft discarded");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const containers = sheet?.tables || (sheet ? [sheet] : []);
  // the breakup set: the saved state from the server unless this session has toggled rows
  const savedBreakups = useMemo(() => new Set(
    containers.flatMap((c) => (c.rows || [])).filter((r) => r.broken_up).map((r) => r.key)),
    [containers]);
  const breakupSet = draft.breakups ? new Set(draft.breakups) : savedBreakups;
  const onToggleBreakup = (key) => {
    // view-only users may expand the breakup read-only; their cells stay disabled
    if (locked) return;
    patch((cur) => {
      const base = cur.breakups ? new Set(cur.breakups) : new Set(savedBreakups);
      if (base.has(key)) base.delete(key); else base.add(key);
      return { ...cur, breakups: [...base] };
    });
  };

  // newly added lines render immediately, inside the table they were added under
  const displayContainers = useMemo(() => containers.map((c) => {
    const mine = (draft.added || []).filter((a) => (a.container || "main") === (c.key || "main"));
    if (!mine.length) return c;
    const rows = [...(c.rows || [])];
    const at = Math.max(rows.length - 1, 0);
    rows.splice(at, 0, ...mine.map((a) => ({
      key: a.key, label: a.label, kind: "line", values: a.values || {},
      columns: c.columns || [], indent: 1, added: true, editable: true,
      dimension: a.dimension, dimension_value: a.dimension_value,
    })));
    return { ...c, rows };
  }), [containers, draft.added]);
  const moveOptions = useMemo(() => {
    if (active === "fixed_cost") return data?.functions || [];
    if (active === "pvc") return data?.categories || [];
    if (active === "dvc" || active === "revenue") return data?.outlets || [];
    return [];
  }, [active, data]);

  const liveTotal = useMemo(() => {
    let total = 0;
    containers.forEach((c) => (c.rows || []).forEach((r) => {
      if (r.kind === "detail") {
        // a ledger line counts only while its component row is broken up
        if (!breakupSet.has(r.parent_key) || r.excluded || draft.excluded.includes(r.key)) return;
      } else {
        if (r.kind !== "line" || r.excluded || draft.excluded.includes(r.key)) return;
        if (breakupSet.has(r.key)) return;   // broken up: its ledger lines carry the value
      }
      (r.block_columns || c.columns || []).forEach((col) => {
        const p = draft.cells[r.key];
        total += nz(p && p[col] !== undefined ? p[col] : r.values?.[col]);
      });
    }));
    (draft.added || []).forEach((a) => {
      // a new line's figures arrive through the cell edits for its key, not its own values
      const merged = { ...(a.values || {}), ...(draft.cells[a.key] || {}) };
      Object.values(merged).forEach((v) => { total += nz(v); });
    });
    return Math.round(total * 100) / 100;
  }, [containers, draft, breakupSet]);

  const control = sheet?.control_total || 0;
  const detail = sheet?.control_detail;
  const difference = control ? Math.round((liveTotal - control) * 100) / 100 : null;
  const dirty = Object.keys(draft.cells).length > 0 || draft.excluded.length > 0
    || draft.added.length > 0 || Object.keys(draft.moves).length > 0 || !!draft.breakups;

  return (
    <FpShell
      title="Review & approve — reconciliation"
      description="The month laid out in the output-pack format. Correct any figure in place; row, column and grand totals move as you type. Approving a sheet makes it the published figure for that area."
      actions={
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}/recon-grids.xlsx?period=${period}`}
           data-testid="fp-recon-download"
           className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold text-navy transition hover:bg-muted">
          <DownloadSimple size={14} /> Download (.xlsx)
        </a>
      }
    >
      <div className="space-y-5" data-testid="fp-recon-grids">
        <div className="panel flex flex-wrap gap-2 p-3">
          {(data?.sheet_order || []).map((key) => {
            const s = data.sheets[key];
            return (
              <button key={key} onClick={() => setActive(key)} data-testid={`fp-recon-tab-${key}`}
                      className={`rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.12em] transition ${
                        active === key ? "bg-navy text-white" : "border border-border text-navy hover:bg-muted"}`}>
                {s.label}
                {s.status === "approved" && <span className="ml-2 text-success">●</span>}
              </button>
            );
          })}
        </div>

        {sheet && (
          <>
            <div className="panel grid gap-4 p-5 md:grid-cols-4">
              {[["Sheet says", liveTotal, "fp-recon-sheet-total"],
                ["Books control total", control || null, "fp-recon-control-total"],
                ["Difference", difference, "fp-recon-difference"],
                ["Published now", sheet.model_total, "fp-recon-model-total"]].map(([label, value, tid]) => (
                <div key={label}>
                  <p className="label-caps">{label}</p>
                  <p className={`mt-1 font-display text-lg ${
                    label === "Difference" ? (Math.abs(nz(value)) < 1 ? "text-success" : "text-destructive") : "text-navy"}`}
                     data-testid={tid}>
                    {value === null || value === undefined ? "—" : money(value)}
                  </p>
                  {label === "Books control total" && !control && (
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      the books carry no single group for this area — tie to Published instead
                    </p>
                  )}
                  {label === "Books control total" && (
                    <button onClick={() => setShowDerivation(true)}
                            data-testid="fp-recon-control-derivation-btn"
                            className="mt-1 text-[10px] uppercase tracking-[0.1em] text-terracotta underline-offset-2 hover:underline">
                      How is this built?
                    </button>
                  )}
                </div>
              ))}
            </div>

            {active === "cogs" && (
              <div className="panel flex flex-wrap items-end justify-between gap-4 p-5"
                   data-testid="fp-recon-netfg-panel">
                <div className="max-w-2xl">
                  <p className="label-caps">Net FG stock — save it alone</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    RM COGS, packing and power &amp; fuel are already signed off under the PVC
                    sheet — only the Net FG movement feeds Level 1 from this tab. Type the figure
                    and save; the four derivation tables below stay untouched. Currently
                    published: <strong className="text-navy">{money(sheet?.net_fg_published)}</strong>.
                    Approving the full COGS sheet later replaces this figure.
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  <input type="number" step="0.01" value={netFg}
                         onChange={(e) => setNetFg(e.target.value)}
                         disabled={!canEdit} placeholder="0" data-testid="fp-recon-netfg-input"
                         className="w-40 rounded-md border border-border bg-card px-3 py-2 text-right text-xs tabular-nums text-navy outline-none transition focus:border-terracotta disabled:opacity-50" />
                  <button onClick={saveNetFg} disabled={!!busy || !canEdit || netFg === ""}
                          data-testid="fp-recon-netfg-save"
                          className="rounded-full bg-navy px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-white transition hover:bg-navy/90 disabled:opacity-50">
                    {busy === "netfg" ? "Saving" : "Save Net FG only"}
                  </button>
                </div>
              </div>
            )}

            <div className="panel overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
                <div>
                  <p className="label-caps">{monthLabel(period)} · {sheet.label}</p>
                  <h3 className="font-display text-lg text-navy">
                    {locked ? "Approved — this sheet is the published figure" : "Draft — nothing published until you approve"}
                  </h3>
                  {locked && sheet.approved_by_email && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      approved by {sheet.approved_by_email} · {String(sheet.approved_at).slice(0, 10)}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => setShowChecklist(true)} data-testid="fp-recon-checklist-btn"
                          className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted">
                    <ListChecks size={11} /> Test checklist
                  </button>
                  {!locked && canEdit && (
                    <>
                      {containers.length === 1 && (
                      <button onClick={() => addLine(containers[0])} data-testid="fp-recon-add-line"
                              className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted">
                        <Plus size={11} /> Add line
                      </button>
                      )}
                      <button onClick={preview} disabled={!!busy} data-testid="fp-recon-preview"
                              className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted disabled:opacity-50">
                        <ArrowsLeftRight size={11} /> {busy === "preview" ? "Checking" : "What changes"}
                      </button>
                      <button onClick={save} disabled={!!busy || !dirty} data-testid="fp-recon-save"
                              className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted disabled:opacity-50">
                        {busy === "save" ? "Saving" : "Save draft"}
                      </button>
                      {data?.can_approve && (
                        <button onClick={approve} disabled={!!busy} data-testid="fp-recon-approve"
                                className="rounded-full bg-terracotta px-5 py-2 text-[10px] uppercase tracking-[0.12em] text-white transition hover:bg-terracotta/90 disabled:opacity-50">
                          {busy === "approve" ? "Approving" : "Approve sheet"}
                        </button>
                      )}
                      {sheet.version > 0 && (
                        <button onClick={() => act("discard")} disabled={!!busy}
                                data-testid="fp-recon-discard"
                                className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground transition hover:bg-muted">
                          Discard draft
                        </button>
                      )}
                    </>
                  )}
                  {locked && data?.can_reopen && (
                    <button onClick={() => act("reopen")} disabled={!!busy}
                            data-testid="fp-recon-reopen"
                            className="rounded-full border border-border px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-navy transition hover:bg-muted">
                      Re-open
                    </button>
                  )}
                </div>
              </div>

              {impact && (
                <div className="border-b border-border bg-muted/30 px-5 py-4" data-testid="fp-recon-impact">
                  <p className="label-caps">If this sheet were approved</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    {["revenue", "pvc", "dvc", "fixed_expenses", "ebitda"].map((k) => (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{k.replace(/_/g, " ")}</p>
                        <p className="text-xs text-navy" data-testid={`fp-recon-impact-${k}`}>
                          {money(impact.current[k])} → <strong>{money(impact.projected[k])}</strong>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {displayContainers.map((container) => (
                <Grid key={container.key || "main"} container={container} sheetKey={active}
                      edits={draft.cells} onEdit={onEdit} onExclude={onExclude} onMove={onMove}
                      moveOptions={moveOptions} locked={locked} canEdit={canEdit}
                      breakups={breakupSet} onToggleBreakup={onToggleBreakup}
                      onAdd={addLine} />
              ))}
            </div>
          </>
        )}
      </div>
      {showChecklist && <ChecklistModal onClose={() => setShowChecklist(false)} />}
      {showDerivation && sheet && (
        <DerivationModal detail={detail} sheetLabel={sheet.label} sheetTotal={liveTotal}
                         control={control} onClose={() => setShowDerivation(false)} />
      )}
    </FpShell>
  );
}
